import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { dayInZone } from '../lib/clock'
import { format } from 'date-fns'
import {
  RefreshCw, TrendingUp, Target, Mountain, Layers,
  Truck, Drill, RotateCw, AlertTriangle, Lock,
} from 'lucide-react'
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'
import { useScope } from '../contexts/ScopeContext'
import { ScopeChip } from './MineScopeSelect'
import { LoadingState, ErrorState } from './ViewState'
import { fmtInt, fmtDurShort } from '../i18n/format'

const BRAND = '#017f96'
const WASTE = '#f59e0b'
const PROJ = '#7c3aed'

const STATUS = {
  op:      { hex: '#17b26a', labelKey: 'common:device_status.operating' },
  paused:  { hex: '#dd2590', labelKey: 'common:device_status.paused' },
  stop:    { hex: '#f79009', labelKey: 'common:device_status.stopped' },
  notelem: { hex: '#8b5cf6', labelKey: 'common:device_status.no_telemetry' },
  down:    { hex: '#f04438', labelKey: 'common:device_status.no_comms' },
}
const BUCKET = {
  downtime:       { hex: '#f04438', labelKey: 'production:dashboard.bucket_downtime' },
  standby:        { hex: '#f59e0b', labelKey: 'production:dashboard.bucket_standby' },
  non_productive: { hex: '#dd2590', labelKey: 'production:dashboard.bucket_non_productive' },
  delay:          { hex: '#1570ef', labelKey: 'production:dashboard.bucket_delay' },
}

const fmtTon = (t) => fmtInt(Number(t || 0))
const fmtNum = (n) => fmtInt(Number(n || 0))
// O dia do telão é o dia da MINA, não o do navegador: a API corta a janela no fuso da
// mina em escopo e devolve qual usou. Com o fuso do navegador, um painel aberto noutro
// fuso virava o dia no meio do turno, e o operador via o telão zerar com a mina a
// trabalhar.
const todayStr = (tz) => dayInZone(tz)

const fmtDur = (s) => fmtDurShort(s)

function Kpi({ icon, label, value, unit, sub, accent }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {icon}{label}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-bold tabular-nums" style={{ color: accent || undefined }}>{value}</span>
        {unit && <span className="text-xs text-slate-400 dark:text-slate-500">{unit}</span>}
      </div>
      {sub && <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 tabular-nums">{sub}</div>}
    </div>
  )
}

function Card({ title, icon, children, className = '' }) {
  return (
    <div className={`bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 ${className}`}>
      {title && (
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3">
          {icon}{title}
        </div>
      )}
      {children}
    </div>
  )
}

function Bar2({ label, value, max, color, unit = 't', fmt = fmtTon }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 text-xs text-slate-500 dark:text-slate-400 shrink-0">{label}</span>
      <div className="flex-1 h-5 rounded bg-slate-100 dark:bg-slate-700/40 overflow-hidden">
        <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-24 text-right text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200 shrink-0">{fmt(value)} {unit}</span>
    </div>
  )
}

const Empty = ({ children }) => (
  <p className="text-sm text-slate-400 dark:text-slate-500 py-6 text-center">{children}</p>
)

export default function ProductionDashboard() {
  const { t } = useTranslation()
  const { dark } = useTheme()
  const { mineScope } = useScope()
  const [date, setDate] = useState(todayStr(''))
  const [followToday, setFollowToday] = useState(true)
  const [shift, setShift] = useState('')
  const [shifts, setShifts] = useState([])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Fuso em que a API cortou esta janela. Nasce vazio (cai no do navegador) e passa a
  // valer assim que a primeira resposta chega, que é quando o painel sabe de que mina
  // está a falar.
  const tz = data?.window?.timezone || ''

  const GRID = dark ? '#334155' : '#e2e8f0'
  const AXIS = dark ? '#94a3b8' : '#64748b'
  const TOOLTIP_STYLE = dark
    ? { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }
    : { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#1e293b' }

  useEffect(() => {
    let alive = true
    fetch('/api/shifts').then(r => r.json())
      .then(d => { if (alive) setShifts(Array.isArray(d) ? d : []) })
      .catch(() => { if (alive) setShifts([]) })
    return () => { alive = false }
  }, [])

  const abortRef = useRef(null)
  const load = useCallback((withSpinner) => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    if (withSpinner) setLoading(true)
    const params = new URLSearchParams({ date })
    if (shift) params.set('shift', shift)
    if (mineScope !== 'all') params.set('mine', String(mineScope))
    fetch(`/api/dashboard?${params.toString()}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      // Falha de poll NÃO vira dia zerado: o telão fica com o último dado bom e mostra
      // o aviso de erro, em vez de renderizar uma produção plausível de zero tonelada.
      .then(d => { setData(d || null); setError(null) })
      .catch(err => { if (err.name !== 'AbortError') setError(err.message) })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
  }, [date, shift, mineScope])

  useEffect(() => {
    load(true)
    return () => abortRef.current?.abort()
  }, [load])
  useEffect(() => {
    const id = setInterval(() => load(false), 15000)
    return () => clearInterval(id)
  }, [load])

  // O telão fica ligado a noite inteira: enquanto o operador não escolher outra data,
  // a virada do dia move o painel para o dia novo (antes ficava preso em ontem).
  useEffect(() => {
    if (!followToday) return undefined
    const id = setInterval(() => {
      const today = todayStr(tz)
      setDate(d => (d === today ? d : today))
    }, 60000)
    return () => clearInterval(id)
  }, [followToday, tz])

  const pickDate = (value) => {
    setDate(value)
    setFollowToday(value === todayStr(tz))
  }

  const realized = data?.realized || {}
  const total = realized.total || 0
  const ore = realized.ore || 0
  const waste = realized.waste || 0
  const target = data?.target || 0
  const projected = data?.projected || 0
  const attainment = data?.attainment || 0
  const projectedFrac = Number(data?.projectedFrac) || 0
  const closedAt = data?.closedAt ? new Date(data.closedAt) : null
  const fleet = data?.fleet || { op: 0, paused: 0, stop: 0, notelem: 0, down: 0, total: 0 }
  const cyc = data?.cycles || { count: 0, avgSeconds: 0, loads: 0 }
  const drill = data?.drilling || { holes: 0, meters: 0 }

  const oreShare = total > 0 ? Math.round((ore / total) * 100) : 0
  const wasteShare = total > 0 ? Math.round((waste / total) * 100) : 0

  const byFront = useMemo(() => {
    const arr = Array.isArray(data?.byFront) ? [...data.byFront] : []
    return arr.map(f => ({ front: f.front || '—', tonnage: f.tonnage || 0 })).sort((a, b) => b.tonnage - a.tonnage)
  }, [data])

  const hourly = useMemo(() => {
    const arr = Array.isArray(data?.hourly) ? data.hourly : []
    return arr.map(h => ({ hour: `${String(h.hour ?? 0).padStart(2, '0')}:00`, tonnage: h.tonnage || 0 }))
  }, [data])

  const stopReasons = useMemo(() => {
    const arr = Array.isArray(data?.stops?.reasons) ? data.stops.reasons : []
    return arr.slice(0, 8)
  }, [data])
  const stopMax = Math.max(1, ...stopReasons.map(r => r.minutes || 0))
  const byBucket = data?.stops?.byBucket || {}

  const barMax = Math.max(total, target, projected, 1)
  const hasProd = total > 0 || target > 0

  return (
    <div className="h-full flex flex-col bg-offwhite dark:bg-slate-950 text-slate-800 dark:text-slate-100">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-3 flex items-center gap-4 flex-wrap">
        <h1 className="text-base font-semibold text-slate-800 dark:text-slate-100">{t('production:dashboard.title')}</h1>
        <ScopeChip />
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <input type="date" value={date} onChange={e => pickDate(e.target.value)}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-700 dark:text-slate-100 dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-brand/40" />
          <select value={shift} onChange={e => setShift(e.target.value)}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-700 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand/40">
            <option value="">{t('production:dashboard.all_day')}</option>
            {shifts.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
          <button onClick={() => load(true)}
            className="inline-flex items-center gap-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-600 dark:text-slate-200 transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {t('common:actions.refresh')}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
        {loading && !data ? (
          <LoadingState label={t('production:dashboard.loading')} className="h-full" />
        ) : error && !data ? (
          <ErrorState message={error} onRetry={() => load(true)} className="h-full" />
        ) : (
          <div className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-status-stop/30 bg-status-stop/10 px-3 py-2 text-xs text-status-stop">
                <AlertTriangle size={14} className="shrink-0" />
                {t('production:dashboard.stale_data', { error })}
              </div>
            )}
            {/* Fechar o período é um marco de conferência, não um travamento: uma
                reentrega da outbox continua a entrar e a mudar estes números, então a
                tela diz quando foi fechado em vez de os apresentar como definitivos. */}
            {closedAt && (
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                <Lock size={14} className="shrink-0" />
                {t('production:dashboard.period_closed', { at: format(closedAt, 'dd/MM HH:mm') })}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <Kpi icon={<Target size={13} />} label={t('production:dashboard.kpi_moved')} value={fmtTon(total)} unit={t('common:units.tons')} accent={BRAND}
                sub={target > 0 ? t('production:dashboard.pct_of_target', { pct: Math.round(attainment) }) : t('production:dashboard.no_target')} />
              <Kpi icon={<Mountain size={13} />} label={t('production:dashboard.kpi_ore')} value={fmtTon(ore)} unit={t('common:units.tons')} accent={BRAND} sub={t('production:dashboard.pct_of_total', { pct: oreShare })} />
              <Kpi icon={<Layers size={13} />} label={t('production:dashboard.kpi_waste')} value={fmtTon(waste)} unit={t('common:units.tons')} accent={WASTE} sub={t('production:dashboard.pct_of_total', { pct: wasteShare })} />
              <Kpi icon={<RotateCw size={13} />} label={t('common:entities.cycles')} value={fmtNum(cyc.count)} sub={cyc.avgSeconds > 0 ? t('production:dashboard.avg_cycle', { dur: fmtDur(cyc.avgSeconds) }) : '—'} />
              <Kpi icon={<Drill size={13} />} label={t('common:entities.holes')} value={fmtNum(drill.holes)} sub={drill.meters > 0 ? t('production:dashboard.meters_drilled', { meters: fmtTon(drill.meters) }) : '—'} />
              <Kpi icon={<Truck size={13} />} label={t('production:dashboard.kpi_active_fleet')} value={`${fleet.op}/${fleet.total}`} sub={t('production:dashboard.operating_now')} accent={STATUS.op.hex} />
            </div>

            <Card title={t('production:dashboard.fleet_status')} icon={<Truck size={13} />}>
              {fleet.total === 0 ? <Empty>{t('production:dashboard.no_equipment')}</Empty> : (
                <div className="flex flex-wrap gap-3">
                  {['op', 'paused', 'stop', 'notelem', 'down'].map(k => (
                    <div key={k} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS[k].hex }} />
                      <span className="text-sm text-slate-600 dark:text-slate-300">{t(STATUS[k].labelKey)}</span>
                      <span className="text-sm font-bold tabular-nums" style={{ color: STATUS[k].hex }}>{fleet[k]}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title={t('production:dashboard.plan_vs_actual')} icon={<TrendingUp size={13} />}>
              {!hasProd ? <Empty>{t('production:dashboard.no_production')}</Empty> : (
                <>
                  <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('production:dashboard.projected_pace')}</div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-4xl font-bold tabular-nums" style={{ color: PROJ }}>{fmtTon(projected)}</span>
                        <span className="text-sm text-slate-400 dark:text-slate-500">{t('production:dashboard.shift_projection')}</span>
                      </div>
                      {/* A projeção é o realizado dividido pela fração do período já
                          decorrida: no início do turno ela oscila muito, e sem dizer quanto
                          do período correu o número lê como previsão firme. */}
                      {projectedFrac > 0 && projectedFrac < 1 && (
                        <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 tabular-nums">
                          {t('production:dashboard.projection_basis', { pct: Math.round(projectedFrac * 100) })}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-slate-400 dark:text-slate-500">{t('production:dashboard.attainment')}</div>
                      <div className="text-2xl font-bold tabular-nums" style={{ color: attainment >= 100 ? STATUS.op.hex : PROJ }}>{Math.round(attainment)}%</div>
                      <div className="text-xs text-slate-400 dark:text-slate-500 tabular-nums">{t('production:dashboard.target_t', { value: fmtTon(target) })}</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Bar2 label={t('production:dashboard.actual')} value={total} max={barMax} color={BRAND} unit={t('common:units.tons')} />
                    <Bar2 label={t('production:dashboard.target')} value={target} max={barMax} color={dark ? '#475569' : '#94a3b8'} unit={t('common:units.tons')} />
                    <Bar2 label={t('production:dashboard.projected')} value={projected} max={barMax} color={PROJ} unit={t('common:units.tons')} />
                  </div>
                </>
              )}
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title={t('production:dashboard.by_front')}>
                {byFront.length === 0 ? <Empty>{t('production:dashboard.no_front_data')}</Empty> : (
                  <ResponsiveContainer width="100%" height={Math.max(180, byFront.length * 38)}>
                    <BarChart data={byFront} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                      <CartesianGrid stroke={GRID} horizontal={false} />
                      <XAxis type="number" tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={fmtTon} axisLine={{ stroke: GRID }} tickLine={false} />
                      <YAxis type="category" dataKey="front" tick={{ fill: AXIS, fontSize: 11 }} width={110} axisLine={false} tickLine={false} />
                      <Tooltip cursor={{ fill: 'rgba(148,163,184,0.08)' }} contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${fmtTon(v)} ${t('common:units.tons')}`, t('production:dashboard.tonnage')]} />
                      <Bar dataKey="tonnage" fill={BRAND} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>

              <Card title={t('production:dashboard.by_hour')}>
                {hourly.length === 0 ? <Empty>{t('production:dashboard.no_hourly_data')}</Empty> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={hourly} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                      <defs>
                        <linearGradient id="prodArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={BRAND} stopOpacity={0.5} />
                          <stop offset="100%" stopColor={BRAND} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={GRID} vertical={false} />
                      <XAxis dataKey="hour" tick={{ fill: AXIS, fontSize: 11 }} axisLine={{ stroke: GRID }} tickLine={false} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={fmtTon} axisLine={false} tickLine={false} width={48} />
                      <Tooltip cursor={{ stroke: GRID }} contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${fmtTon(v)} ${t('common:units.tons')}`, t('production:dashboard.tonnage')]} />
                      <Area type="monotone" dataKey="tonnage" stroke={BRAND} strokeWidth={2} fill="url(#prodArea)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </div>

            <Card title={t('production:dashboard.stops_top_reasons')} icon={<AlertTriangle size={13} />}>
              {stopReasons.length === 0 ? <Empty>{t('production:dashboard.no_stops')}</Empty> : (
                <>
                  <div className="flex flex-wrap gap-3 mb-4">
                    {['downtime', 'standby', 'non_productive', 'delay'].map(b => (
                      <div key={b} className="flex items-center gap-2 text-sm">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: BUCKET[b].hex }} />
                        <span className="text-slate-500 dark:text-slate-400">{t(BUCKET[b].labelKey)}</span>
                        <span className="font-semibold tabular-nums text-slate-700 dark:text-slate-200">{fmtDur((byBucket[b] || 0) * 60)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {stopReasons.map((r, i) => {
                      const color = (BUCKET[r.bucket] || { hex: '#64748b' }).hex
                      const pct = Math.min(100, (r.minutes / stopMax) * 100)
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="w-40 text-xs text-slate-600 dark:text-slate-300 truncate shrink-0" title={r.label}>{r.label}</span>
                          <div className="flex-1 h-5 rounded bg-slate-100 dark:bg-slate-700/40 overflow-hidden">
                            <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: color }} />
                          </div>
                          <span className="w-28 text-right text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200 shrink-0">
                            {t('production:dashboard.stop_min_count', { minutes: Math.round(r.minutes), times: r.count })}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
